import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { ClipboardList, Play, AlertTriangle, ArrowRight, RefreshCw, Link as LinkIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLE = {
    ASSIGNED: "bg-amber-50 text-amber-700 border-amber-200",
    OVERDUE: "bg-red-50 text-[#E02B2B] border-red-200",
    FLAGGED_IN_PERSON: "bg-purple-50 text-purple-700 border-purple-200",
    COMPLETED: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function CoachingQueuePage() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [statusFilter, setStatusFilter] = useState("OPEN");
    const isHO = user?.role === "HO_ADMIN";

    async function load() {
        setLoading(true);
        try {
            const params = statusFilter === "OPEN" ? {} : { status: statusFilter };
            const { data } = await api.get("/coaching/assignments", { params });
            let filtered = data;
            if (statusFilter === "OPEN") filtered = data.filter((r) => ["ASSIGNED", "OVERDUE", "FLAGGED_IN_PERSON"].includes(r.status));
            setRows(filtered);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [statusFilter]);

    async function scanNow() {
        setScanning(true);
        try {
            const { data } = await api.post("/coaching/scan-now");
            toast.success(`Scan complete — ${data.assignments_created} new assignment(s)`);
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setScanning(false); }
    }

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Coaching</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2"><ClipboardList className="w-6 h-6" /> Coaching queue</h1>
                    <p className="text-sm text-gray-500 mt-1">Refreshers auto-assigned when the checklist shows a weakness. Visible only to managers, PMs and HO — never to staff or clients.</p>
                </div>
                {isHO && (
                    <div className="flex items-center gap-2">
                        <Link to="/admin/coaching-mapping" className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-[#1E3355] text-sm font-semibold px-4 py-2 rounded-md hover:border-[#1E3355]" data-testid="coaching-mapping-link">
                            <LinkIcon className="w-4 h-4" /> Mapping
                        </Link>
                        <button onClick={scanNow} disabled={scanning} className="inline-flex items-center gap-1.5 bg-white border border-[#1E3355] text-[#1E3355] text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#F4F6F9] disabled:opacity-60" data-testid="coaching-scan-now">
                            <RefreshCw className={`w-4 h-4 ${scanning ? "animate-spin" : ""}`} /> Scan now
                        </button>
                    </div>
                )}
            </div>

            <div className="mt-5 flex flex-wrap gap-2" data-testid="coaching-filter-row">
                {["OPEN", "ASSIGNED", "OVERDUE", "FLAGGED_IN_PERSON", "COMPLETED"].map((s) => (
                    <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${statusFilter === s ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-600 border-gray-200 hover:border-[#1E3355]"}`} data-testid={`coaching-filter-${s}`}>{s.replace("_", " ")}</button>
                ))}
            </div>

            {loading ? <div className="mt-6 h-40 rounded-xl bg-white animate-pulse" /> :
             rows.length === 0 ? <div className="mt-6 rounded-xl bg-white border border-gray-100 p-10 text-center text-sm text-gray-500" data-testid="coaching-empty">No assignments in this view.</div> :
             <div className="mt-5 space-y-3" data-testid="coaching-list">
                {rows.map((r) => (
                    <div key={r.id} className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid={`coaching-row-${r.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_STYLE[r.status] || "bg-gray-100 text-gray-700 border-gray-200"}`}>{r.status.replace("_", " ")}</span>
                                    <span className="text-[11px] font-semibold text-gray-500">{r.site_name}</span>
                                    <span className="text-[11px] text-gray-400">·</span>
                                    <span className="text-[11px] font-semibold text-[#1E3355]">{r.role}</span>
                                    {r.flagged_for_in_person && <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200"><AlertTriangle className="w-3 h-3" /> In-person coaching needed</span>}
                                </div>
                                <div className="font-display font-semibold text-[#1E3355] mt-1">{r.course_title}</div>
                                <div className="text-xs text-gray-700 mt-1" data-testid={`coaching-reason-${r.id}`}>{r.reason}</div>
                                <div className="text-[11px] text-gray-500 mt-1">Due by {fmtDate(r.due_by)} · Fail count {r.fail_count || 0}</div>
                                {r.status === "COMPLETED" && r.completed_by_staff_name && (
                                    <div className="text-[11px] text-emerald-700 mt-1">Completed by {r.completed_by_staff_name} · Score {r.completed_score}/5 · {fmtDate(r.completed_at)}</div>
                                )}
                            </div>
                            {r.status !== "COMPLETED" && (
                                <Link to={`/training/${r.course_id}?assignment=${r.id}`} className="inline-flex items-center gap-1 bg-[#1E3355] hover:bg-[#15243C] text-white text-xs font-semibold px-3 py-2 rounded-md" data-testid={`coaching-take-${r.id}`}>
                                    <Play className="w-3.5 h-3.5" /> Take now <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                            )}
                        </div>
                    </div>
                ))}
             </div>}
        </div>
    );
}
